import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { getCompanyCostCodes } from "../app/apis/subRatingsAPI";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import { ICostCode } from "../app/interfaces/ICostCode";
import { RootState } from "../app/store";

export interface CostCodesState {
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    data: ICostCode[];
    error?: IErrorState;
}

const initialState: CostCodesState = {
    status: 'idle',
    data: [],
    error: undefined
};

export const getCompanyCostCodesThunk = createAsyncThunk(
    'costCodes/getCompanyCostCodes',
    async (companyId: number, { rejectWithValue }) => {
        try {
            return await getCompanyCostCodes(companyId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const costCodesSlice = createSlice({
    name: 'costCodes',
    initialState,
    reducers: {
        clearCostCodesError: (state) => {
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCompanyCostCodesThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getCompanyCostCodesThunk.rejected, (state, action) => {
                state.status = 'failed';
                const errorResponse = action?.payload as IErrorState;
                state.error = errorResponse;
            })
            .addCase(getCompanyCostCodesThunk.fulfilled, (state, action) => {
                // The API returns the array directly, not wrapped in a response object
                state.data = action.payload as ICostCode[];
                state.status = 'succeeded';
            });
    }
});

export const { clearCostCodesError } = costCodesSlice.actions;

export const selectCostCodes = (state: RootState) => state.costCodes.data;
export const selectCostCodesStatus = (state: RootState) => state.costCodes.status;
export const selectCostCodesError = (state: RootState) => state.costCodes.error;

export default costCodesSlice.reducer;
